import React from "react";
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    Chip,
    Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Fade from "@mui/material/Fade";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";

const cardSx = {
    position: "relative",
    overflow: "hidden",
    borderRadius: "24px",
    p: 2.2,
    background: "linear-gradient(180deg, #fff 0%, #fff8fa 100%)",
    border: "1px solid rgba(90,28,47,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
};

const labelSx = {
    fontSize: 11,
    fontWeight: 800,
    color: "rgb(90,28,47)",
    letterSpacing: "1.2px",
    mb: 1,
};

const valueSx = {
    fontSize: 15,
    fontWeight: 700,
    color: "#222",
    lineHeight: 1.5,
    wordBreak: "break-word",
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

const formatLabel = (key = "") =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

export function MobileViewDataModal({ open, onClose, data }) {
    if (!data) return null;

    const valueEntries = Object.entries(data.values || {});

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            TransitionComponent={Fade}
            transitionDuration={{ enter: 350, exit: 250 }}
            PaperProps={{
                sx: {
                    background: "linear-gradient(180deg, #fff 0%, #f9f4f6 100%)",
                    overflow: "hidden",
                },
            }}
        >
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: -120,
                        right: -100,
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(90,28,47,0.18) 0%, transparent 70%)",
                        zIndex: 0,
                    }}
                />

                <Box
                    sx={{
                        px: 3,
                        pt: 4,
                        pb: 3,
                        background:
                            "linear-gradient(135deg, rgb(90,28,47) 0%, rgb(130,45,72) 100%)",
                        color: "#fff",
                        position: "relative",
                        overflow: "hidden",
                        borderBottomLeftRadius: "28px",
                        borderBottomRightRadius: "28px",
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: -40,
                            right: -30,
                            width: 140,
                            height: 140,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.08)",
                        }}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            position: "relative",
                            zIndex: 2,
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    mb: 1.5,
                                }}
                            >
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        background: "rgba(255,255,255,0.15)",
                                        backdropFilter: "blur(10px)",
                                    }}
                                >
                                    <Inventory2RoundedIcon />
                                </Avatar>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: 17,
                                            fontWeight: 800,
                                            lineHeight: 1.2,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {data.identifier || "—"}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            opacity: 0.75,
                                            mt: 0.5,
                                        }}
                                    >
                                        Created {formatDate(data.createdAt)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Chip
                                label="Data Record"
                                size="small"
                                sx={{
                                    background: "rgba(255,255,255,0.14)",
                                    color: "#fff",
                                    fontWeight: 700,
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    backdropFilter: "blur(10px)",
                                }}
                            />
                        </Box>

                        <IconButton
                            onClick={onClose}
                            sx={{
                                width: 42,
                                height: 42,
                                background: "rgba(255,255,255,0.12)",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.15)",
                                backdropFilter: "blur(10px)",
                                "&:hover": {
                                    background: "rgba(255,255,255,0.18)",
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        px: 2,
                        py: 3,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <Box sx={cardSx}>
                        <Box
                            sx={{
                                position: "absolute",
                                top: -40,
                                right: -30,
                                width: 100,
                                height: 100,
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle, rgba(90,28,47,0.08) 0%, transparent 70%)",
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                mb: 2,
                                borderBottom: "1px solid rgba(90,28,47,0.08)",
                                pb: 1,
                            }}
                        >
                            <Typography sx={labelSx}>
                                IDENTIFIER
                            </Typography>

                            <Typography
                                sx={{
                                    ...valueSx,
                                    textAlign: "right",
                                    wordBreak: "break-word",
                                }}
                            >
                                {formatValue(data.identifier)}
                            </Typography>
                        </Box>

                        <Typography sx={labelSx}>VALUES :</Typography>

                        {valueEntries.length > 0 ? (
                            valueEntries
                                .map(([key, value]) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            gap: 2,
                                            py: 1.2,
                                            borderBottom: "1px solid rgba(90,28,47,0.08)",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: "rgb(90,28,47)",
                                                minWidth: 110,
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {formatLabel(key)}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                ...valueSx,
                                                textAlign: "right",
                                                flex: 1,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {formatValue(value)}
                                        </Typography>
                                    </Box>
                                ))
                        ) : (
                            <Typography sx={valueSx}>—</Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}